import api from './api';
import type {
  InsightsOverview,
  InsightsLead,
  InsightsLeadEvent,
  InsightsLeadQuery,
  InsightsDateRange,
} from '../types';

interface LeadsResponse {
  data: InsightsLead[];
  total: number;
  page: number;
  pageSize: number;
}

interface ExportResponse {
  csv: string;
  filename: string;
  rowCount: number | null;
}

const buildParams = (query: object): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'all') return;
    params.append(key, String(value));
  });
  return params.toString();
};

export const insightsService = {
  async getOverview(range: InsightsDateRange): Promise<InsightsOverview> {
    const response = await api.get<InsightsOverview>(`/company/insights/overview?${buildParams(range)}`);
    return response.data;
  },

  async getLeads(query: InsightsLeadQuery): Promise<LeadsResponse> {
    const response = await api.get<LeadsResponse>(`/company/insights/leads?${buildParams(query)}`);
    return response.data;
  },

  async getLeadTimeline(leadId: string): Promise<InsightsLeadEvent[]> {
    const response = await api.get<{ events: InsightsLeadEvent[] }>(
      `/company/insights/leads/${leadId}/timeline`
    );
    return response.data.events;
  },

  /**
   * The API returns CSV text in the JSON body; turning it into a download is a
   * client concern so the request still flows through the authenticated axios
   * instance (headers, sysadmin company context, token refresh).
   */
  async exportCsv(type: 'leads' | 'metrics', query: InsightsLeadQuery): Promise<ExportResponse> {
    const response = await api.get<ExportResponse>(
      `/company/insights/export?${buildParams({ ...query, type })}`
    );
    return response.data;
  },
};

/** Trigger a browser download for CSV text returned by the export endpoint. */
export const downloadCsv = (csv: string, filename: string): void => {
  // The BOM keeps Excel from mangling non-ASCII names on open.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default insightsService;
